import { Appearance } from "@ui/Appearance";
import { useAppContext } from "@ui/layouts/layoutProvider";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import type { TextColor } from "@ui/typography/Text";
import { createEffect, For, onCleanup, Show } from "solid-js";
import {
	type Toast,
	type ToastPosition,
	type ToastType,
	toastStore,
} from "./toastStore";
import "./Toasts.css";

const DEFAULT_SYMBOL: Record<ToastType, string> = {
	success: "check_circle",
	info: "info",
	warning: "warning",
	error: "error",
};

const SYMBOL_COLOR: Record<ToastType, TextColor> = {
	success: "success",
	info: "primary",
	warning: "warning",
	error: "error",
};

type ToastItemProps = {
	toast: Toast;
};

const ToastItem = (props: ToastItemProps) => {
	const { menuIsOpen } = useAppContext();
	const { toast } = props;
	let timeoutId: number | undefined;

	// Auto-close functionality
	createEffect(() => {
		if (toast.duration && toast.duration > 0) {
			timeoutId = window.setTimeout(() => {
				toastStore.hideToast(toast.id);
			}, toast.duration);
		}

		onCleanup(() => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		});
	});

	const handleClose = () => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		toastStore.hideToast(toast.id);
	};

	const getOffsetStyle = () => {
		if (!toast.offset) {
			const position = props.toast.position ?? "bottom-left";
			if (!position.endsWith("-left")) {
				return {};
			}
			const rem = 16;
			const offset = menuIsOpen() ? rem * 19 : rem * 4;
			return { left: `${offset}px` };
		}

		const [x, y] = toast.offset;
		const position = toast.position ?? "bottom-left";
		const baseOffset = 16; // 1rem in pixels

		const style: Record<string, string> = {
			position: "fixed",
		};

		switch (position) {
			case "top-left":
				style.left = `${baseOffset + x}px`;
				style.top = `${baseOffset + y}px`;
				break;
			case "top-right":
				style.right = `${baseOffset + x}px`;
				style.top = `${baseOffset + y}px`;
				break;
			case "top-center":
				style.left = "50%";
				style.top = `${baseOffset + y}px`;
				style.transform = `translateX(calc(-50% + ${x}px))`;
				break;
			case "bottom-left":
				style.left = `${baseOffset + x}px`;
				style.width = "100%";
				style.bottom = `${baseOffset + y}px`;
				break;
			case "bottom-right":
				style.right = `${baseOffset + x}px`;
				style.bottom = `${baseOffset + y}px`;
				break;
			case "bottom-center":
				style.left = "50%";
				style.bottom = `${baseOffset + y}px`;
				style.transform = `translateX(calc(-50% + ${x}px))`;
				break;
		}

		return style;
	};

	const combinedStyle = () => {
		return getOffsetStyle();
	};

	return (
		<Appearance
			scheme="inverse"
			class="gvtui-toast"
			data-type={toast.type}
			data-has-offset={toast.offset ? "true" : undefined}
			style={combinedStyle()}
		>
			<div class="gvtui-toast__content">
				<div class="gvtui-toast__icon">
					<MaterialSymbol
						symbol={toast.symbol ?? DEFAULT_SYMBOL[toast.type]}
						color={SYMBOL_COLOR[toast.type]}
						filled={toast.filled ?? true}
						relativeSize="even-larger"
					/>
				</div>
				<div class="gvtui-toast__text">
					<div class="gvtui-toast__title">{toast.title}</div>
					<Show when={toast.description}>
						<div class="gvtui-toast__description">
							{typeof toast.description === "function" ? (
								toast.description({ toast, handleClose })
							) : (
								<span>{toast.description}</span>
							)}
						</div>
					</Show>
				</div>
				<button
					type="button"
					class="gvtui-toast__close"
					onClick={handleClose}
					aria-label="Close notification"
				>
					×
				</button>
			</div>
		</Appearance>
	);
};

type ToastContainerProps = {
	position?: ToastPosition;
};

export const ToastContainer = (props: ToastContainerProps) => {
	const position = () => props.position ?? "bottom-left";

	// Group toasts by position (exclude toasts with custom offsets)
	// Reverse order so newest toasts appear first (at base position)
	const toastsForPosition = () => {
		return toastStore
			.getActiveToasts()
			.filter(
				(toast) =>
					toast.active &&
					!toast.offset &&
					(toast.position ?? "bottom-left") === position(),
			)
			.reverse();
	};

	return (
		<div class={`gvtui-toast-container gvtui-toast-container--${position()}`}>
			<For each={toastsForPosition()}>
				{(toast) => <ToastItem toast={toast} />}
			</For>
		</div>
	);
};

/**
 * Viewport overlay for active toasts. Mount once in the app shell.
 *
 * [Design System →](http://localhost:5174/component/Toast)
 */
export const Toasts = () => {
	const positions: ToastPosition[] = [
		"top-left",
		"top-right",
		"top-center",
		"bottom-left",
		"bottom-right",
		"bottom-center",
	];

	// Get toasts with custom offsets (positioned absolutely)
	const offsetToasts = () =>
		toastStore
			.getActiveToasts()
			.filter((toast) => toast.active && toast.offset);

	return (
		<>
			{/* Render toasts with custom offsets */}
			<For each={offsetToasts()}>{(toast) => <ToastItem toast={toast} />}</For>
			{/* Render toasts in containers by position */}
			<For each={positions}>
				{(pos) => {
					const toastsForPos = () =>
						toastStore
							.getActiveToasts()
							.filter(
								(toast) =>
									toast.active &&
									!toast.offset &&
									(toast.position ?? "bottom-left") === pos,
							);
					return (
						<Show when={toastsForPos().length > 0}>
							<ToastContainer position={pos} />
						</Show>
					);
				}}
			</For>
		</>
	);
};
